import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { Wrench, Hammer, ArrowRight } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { AddressAutocomplete } from "@/components/AddressAutocomplete";
import { Captcha } from "@/components/Captcha";
import api, { apiError } from "@/lib/api";
import { useAuth, dashboardPath } from "@/context/AuthContext";

const PRO_ROLES = [
  { key: "intervenant", label: "Technicien d'entretien", icon: Wrench, desc: "Je nettoie et entretiens les climatiseurs." },
  { key: "poseur", label: "Installateur", icon: Hammer, desc: "J'installe des climatiseurs et j'achète des demandes." },
];

export default function RegisterPro() {
  const [role, setRole] = useState("intervenant");
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", company: "" });
  const [base, setBase] = useState({ base_address: "", base_city: "", base_lat: null, base_lon: null });
  const [captcha, setCaptcha] = useState({ token: "", answer: "", website: "" });
  const [busy, setBusy] = useState(false);
  const { loginWithToken } = useAuth();
  const navigate = useNavigate();
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!captcha.answer) return toast.error("Merci de répondre à la question anti-spam");
    if (!base.base_address) return toast.error("Indiquez votre zone d'intervention");
    setBusy(true);
    try {
      const payload = { ...form, role, ...base, captcha_token: captcha.token, captcha_answer: captcha.answer, website: captcha.website };
      const { data } = await api.post("/auth/register", payload);
      loginWithToken(data.token, data.user);
      toast.success("Bienvenue dans le réseau Climateasy !");
      navigate(dashboardPath(data.user.role));
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PublicLayout>
      <section className="mx-auto max-w-lg px-4 py-16">
        <h1 className="text-3xl font-extrabold text-[#263871]">Rejoindre le réseau</h1>
        <p className="mt-2 text-slate-600">Recevez des missions d'entretien et des demandes d'installation près de chez vous.</p>

        <div className="mt-6 grid grid-cols-2 gap-3">
          {PRO_ROLES.map((r) => (
            <button key={r.key} type="button" data-testid={`pro-role-${r.key}`} onClick={() => setRole(r.key)} className={`flex flex-col items-center gap-1.5 rounded-2xl border-2 p-4 text-center transition-colors ${role === r.key ? "border-[#1D9BF0] bg-[#E0F2FE]" : "border-slate-200 bg-white"}`}>
              <r.icon className={`h-6 w-6 ${role === r.key ? "text-[#1D9BF0]" : "text-slate-400"}`} />
              <span className="text-xs font-bold text-[#263871]">{r.label}</span>
            </button>
          ))}
        </div>
        <p className="mt-2 text-center text-xs text-slate-400">{PRO_ROLES.find((r) => r.key === role)?.desc}</p>

        <form onSubmit={submit} className="mt-6 space-y-4">
          <div>
            <Label htmlFor="name">Nom complet</Label>
            <Input id="name" data-testid="register-name" required value={form.name} onChange={set("name")} className="mt-1 h-12 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="register-email" type="email" required value={form.email} onChange={set("email")} className="mt-1 h-12 rounded-xl" />
          </div>
          <div>
            <Label htmlFor="password">Mot de passe</Label>
            <Input id="password" data-testid="register-password" type="password" required minLength={6} value={form.password} onChange={set("password")} className="mt-1 h-12 rounded-xl" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="phone">Téléphone</Label>
              <Input id="phone" data-testid="register-phone" value={form.phone} onChange={set("phone")} className="mt-1 h-12 rounded-xl" />
            </div>
            <div>
              <Label htmlFor="company">Entreprise (option)</Label>
              <Input id="company" data-testid="register-company" value={form.company} onChange={set("company")} className="mt-1 h-12 rounded-xl" />
            </div>
          </div>
          <div>
            <Label>Zone d'intervention (ville/adresse de base)</Label>
            <p className="mb-1 text-xs text-slate-400">Apparaît sur la carte du réseau Climateasy.</p>
            <AddressAutocomplete testid="register-zone" value={base.base_address} onSelect={({ label, lat, lon }) => setBase({ base_address: label, base_city: label.split(" ").slice(-1)[0], base_lat: lat, base_lon: lon })} placeholder="Ville d'intervention" />
          </div>
          <Captcha testid="register-captcha" onChange={(v) => setCaptcha((p) => ({ ...p, ...v }))} />
          <Button data-testid="register-submit" type="submit" disabled={busy} className="h-12 w-full rounded-full bg-[#263871] hover:bg-[#35508F]">
            {busy ? "Création…" : "Créer mon compte pro"}
          </Button>
        </form>
        <p className="mt-6 text-center text-sm text-slate-600">Déjà inscrit ? <Link to="/connexion" className="font-semibold text-[#1D9BF0]">Connexion</Link></p>
        <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 p-4 text-center">
          <p className="text-sm text-slate-600">Vous cherchez à entretenir votre clim ?</p>
          <Link to="/inscription" data-testid="goto-client" className="mt-1 inline-flex items-center gap-1 text-sm font-semibold text-[#1D9BF0]">Créer un compte client <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </section>
    </PublicLayout>
  );
}
